import { useCallback, useMemo, useState } from "react";

export type FormNavigationStage<T extends string> = {
  readonly current: T;
  push: (next: T) => void;
  pop: () => void;
  reset: (next: T) => void;
  replace: (next: T) => void;
};

/**
 * Stack-based stage navigation for multi-step flows (see
 * `.agents/skills/tycho-multipart-form-navigation/SKILL.md`).
 */
export function useFormNavigation<T extends string>(
  initial: T,
): {
  stage: FormNavigationStage<T>;
  stack: readonly T[];
} {
  const [stack, setStack] = useState<T[]>(() => [initial]);

  const push = useCallback((next: T) => {
    setStack((s) => [...s, next]);
  }, []);

  const pop = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const reset = useCallback((next: T) => {
    setStack([next]);
  }, []);

  const replace = useCallback((next: T) => {
    setStack((s) => (s.length === 0 ? [next] : [...s.slice(0, -1), next]));
  }, []);

  const last = stack.length > 0 ? stack[stack.length - 1] : undefined;
  if (last === undefined) {
    throw new Error("useFormNavigation: empty stack");
  }
  const current = last;

  const stage = useMemo(
    (): FormNavigationStage<T> => ({
      current,
      push,
      pop,
      reset,
      replace,
    }),
    [current, push, pop, reset, replace],
  );

  return { stage, stack };
}
